import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { FaSpinner, FaHome, FaSearch, FaFilePdf, FaFileCode, FaBan } from 'react-icons/fa';
import { IoIosSend } from "react-icons/io";
import CreateNfseModal from "./CreateNfseModal";

const NfseControlScreen = () => {
    // Filtros
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('Todas');
    const [selectedEmitenteId, setSelectedEmitenteId] = useState('');

    // Dados
    const [allNfses, setAllNfses] = useState([]);
    const [currentNfses, setCurrentNfses] = useState([]);
    const [emitentes, setEmitentes] = useState([]);

    // Paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [nfsesPerPage] = useState(8);
    const [totalPages, setTotalPages] = useState(0);

    const [loading, setLoading] = useState(false);
    const [loadingEmitentes, setLoadingEmitentes] = useState(false);

    const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
    const navigate = useNavigate();

    const [modalOpen, setModalOpen] = useState(false);

    // Datas padrão + emitente
    useEffect(() => {
        const storedEmitenteId = localStorage.getItem('userIdEmitente');
        if (storedEmitenteId) {
            setSelectedEmitenteId(storedEmitenteId);
            const today = new Date();
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(today.getDate() - 30);
            setEndDate(format(today, 'yyyy-MM-dd'));
            setStartDate(format(thirtyDaysAgo, 'yyyy-MM-dd'));
        } else {
            toast.error('ID do emitente não encontrado. Faça login novamente.');
        }
    }, []);

    // Emitentes
    useEffect(() => {
        const fetchEmitentes = async () => {
            setLoadingEmitentes(true);
            try {
                const { data } = await axios.get(`${URL_BACKEND}/emitentesAll/999/999`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` }
                });
                setEmitentes(data);
            } catch (err) {
                console.error(err);
                toast.error('Erro ao carregar a lista de emitentes.');
            } finally {
                setLoadingEmitentes(false);
            }
        };
        fetchEmitentes();
    }, [URL_BACKEND]);

    // Buscar NFS-e
    const fetchNfses = async () => {
        if (!selectedEmitenteId || !startDate || !endDate) {
            toast.error('Selecione um emitente e preencha as datas.');
            return;
        }
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${URL_BACKEND}/nfse/${startDate}/${endDate}/Todas/${selectedEmitenteId}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` } }
            );

            setAllNfses(data);
            setTotalPages(Math.ceil(data.length / nfsesPerPage));
            setCurrentPage(1);
            toast.success(`NFS-e carregadas com sucesso! (${data.length} registros)`);
        } catch (error) {
            console.error('Erro ao buscar NFS-e:', error);
            toast.error(error.response?.data?.message || 'Erro ao carregar NFS-e.');
            setAllNfses([]);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    // Atualiza página atual
    useEffect(() => {
        const end = currentPage * nfsesPerPage;
        const start = end - nfsesPerPage;
        setCurrentNfses(allNfses.slice(start, end));
    }, [allNfses, currentPage, nfsesPerPage]);

    // Busca inicial e quando filtros mudam
    useEffect(() => {
        if (selectedEmitenteId && startDate && endDate) {
            fetchNfses();
        }
    }, [selectedEmitenteId, startDate, endDate, URL_BACKEND, statusFilter]);

    // Paginação
    const goToNextPage = () => currentPage < totalPages && setCurrentPage(p => p + 1);
    const goToPreviousPage = () => currentPage > 1 && setCurrentPage(p => p - 1);
    const goToPage = (n) => setCurrentPage(n);

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const max = 5;
        let start = Math.max(1, currentPage - Math.floor(max / 2));
        let end = Math.min(totalPages, start + max - 1);
        if (end - start + 1 < max) start = Math.max(1, end - max + 1);
        for (let i = start; i <= end; i++) pageNumbers.push(i);

        return (
            <ul style={ui.paginationList}>
                {start > 1 && (
                    <>
                        <li style={ui.paginationItem} onClick={() => goToPage(1)}>
                            <button style={ui.paginationButton}>1</button>
                        </li>
                        {start > 2 && <li style={ui.paginationEllipsis}>…</li>}
                    </>
                )}
                {pageNumbers.map(n => (
                    <li key={n} style={ui.paginationItem} onClick={() => goToPage(n)}>
                        <button style={{ ...ui.paginationButton, ...(currentPage === n ? ui.paginationButtonActive : {}) }}>
                            {n}
                        </button>
                    </li>
                ))}
                {end < totalPages && (
                    <>
                        {end < totalPages - 1 && <li style={ui.paginationEllipsis}>…</li>}
                        <li style={ui.paginationItem} onClick={() => goToPage(totalPages)}>
                            <button style={ui.paginationButton}>{totalPages}</button>
                        </li>
                    </>
                )}
            </ul>
        );
    };

    // Ações
    const handleCancelNfse = async (nfseId) => {
        if (!window.confirm(`Tem certeza que deseja cancelar a NFS-e de ID ${nfseId}? Esta ação é irreversível.`)) return;
        toast.loading('Cancelando NFS-e...', { id: 'cancelNfse' });
        try {
            await axios.delete(`${URL_BACKEND}/cancelarNfse/${nfseId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` }
            });
            toast.success(`NFS-e ${nfseId} cancelada com sucesso!`, { id: 'cancelNfse' });
            fetchNfses();
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || `Erro ao cancelar NFS-e ${nfseId}.`, { id: 'cancelNfse' });
        }
    };

    const handleShowPdf = (caminhoPdf) => {
        if (caminhoPdf) window.open(caminhoPdf, '_blank');
        else toast.error('Caminho do PDF não disponível para esta NFS-e.');
    };

    const handleShowXml = (caminhoXml) => {
        alert('Nota sera enviada para a prefeitura')
    };

    return (
        <div style={ui.page}>
            {/* Header com gradiente (mesmo do modal) */}
            <div style={ui.headerBar}>
                <h1 style={ui.headerTitle}>Controle de NFS-e</h1>
                <button onClick={() => navigate('/dashboard')} style={ui.headerButton}>
                    <FaHome style={{ marginRight: 8 }} /> Voltar
                </button>
            </div>

            {/* Filtros em “card” branco */}
            <div style={ui.card}>
                <h3 style={ui.sectionTitle}>Filtros</h3>
                <div style={ui.filtersRow}>
                    <div style={ui.formGroup}>
                        <label style={ui.label}>Data Inicial</label>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={ui.input} />
                    </div>
                    <div style={ui.formGroup}>
                        <label style={ui.label}>Data Final</label>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={ui.input} />
                    </div>
                    <div style={ui.formGroup}>
                        <label style={ui.label}>Status</label>
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={ui.select}>
                            <option value="Todas">Todas</option>
                            <option value="Enviada">Enviada</option>
                            <option value="cancelada">Cancelada</option>
                            <option value="Pendente">Pendente</option>
                        </select>
                    </div>
                    <div style={ui.formGroup}>
                        <label style={ui.label}>Emitente</label>
                        <select
                            value={selectedEmitenteId}
                            onChange={e => setSelectedEmitenteId(e.target.value)}
                            style={ui.select}
                            disabled={loadingEmitentes}
                        >
                            {loadingEmitentes ? (
                                <option value="">Carregando...</option>
                            ) : (
                                <>
                                    <option value="">Selecione...</option>
                                    {emitentes.map(em => (
                                        <option key={em.id} value={em.id}>{em.nome}</option>
                                    ))}
                                </>
                            )}
                        </select>
                        {loadingEmitentes && (
                            <div style={ui.helpText}>
                                <FaSpinner className="fa-spin" style={{ marginRight: 4 }} />
                                Carregando emitentes...
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button onClick={fetchNfses} disabled={loading || loadingEmitentes} style={ui.buttonPrimary}>
                            {loading ? <><FaSpinner className="fa-spin" style={{ marginRight: 8 }} /> Buscando…</> : <><FaSearch style={{ marginRight: 8 }} /> Buscar NFS-e</>}
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabela */}
            <div style={ui.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={ui.sectionTitleTabela}>Resultados</h3>
                    {/* Modal incluir nota */}
                    <button onClick={() => setModalOpen(true)} style={ui.buttonPrimary}>
                        Nova NFS-e
                    </button>
                </div>

                {loading && <p style={ui.muted}>Carregando NFS-e…</p>}
                {!loading && allNfses.length === 0 && <p style={ui.errorText}>Nenhuma NFS-e encontrada com os filtros selecionados.</p>}

                {!loading && allNfses.length > 0 && (
                    <>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={ui.table}>
                                <thead>
                                    <tr>
                                        <th style={ui.th}>Número</th>
                                        <th style={ui.th}>Data</th>
                                        <th style={ui.th}>Cliente</th>
                                        <th style={ui.th}>Valor</th>
                                        <th style={ui.th}>Status</th>
                                        <th style={ui.th}>Cidade</th>
                                        <th style={ui.th}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentNfses.map(nfse => (
                                        <tr key={nfse.id} style={ui.tr}>
                                            <td style={ui.td}>{nfse.id}</td>
                                            <td style={ui.td}>{nfse.data ? format(parseISO(nfse.data), 'dd/MM/yyyy') : '-'}</td>
                                            <td style={ui.td}>{nfse.cliente}</td>
                                            <td style={ui.td}>
                                                {nfse.valor != null
                                                    ? Number(nfse.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                                    : '-'}
                                            </td>
                                            <td style={ui.td}>
                                                <span style={String(nfse.status).toLowerCase() === 'gerada'
                                                    ? ui.badgeGreen
                                                    : ui.badgeRed}>
                                                    {nfse.status}
                                                </span>
                                            </td>
                                            <td style={ui.td}>{nfse.cidade}</td>
                                            <td style={ui.tdActions}>
                                                {(String(nfse.status).toLowerCase() !== 'cancelada' && String(nfse.status).toLowerCase() !== 'cancelado') && (
                                                    <button title="Cancelar NFS-e" onClick={() => handleCancelNfse(nfse.id)} style={ui.actionDanger}>
                                                        <FaBan />
                                                    </button>
                                                )}
                                                {nfse.status === 'Gerada' && (
                                                    <button title="Abrir PDF" onClick={() => handleShowPdf(nfse.caminho_pdf)} style={ui.actionPrimary}>
                                                        <FaFilePdf />
                                                    </button>
                                                )}
                                                {nfse.status === 'Gerada' && (
                                                    <button title="Abrir XML" onClick={() => handleShowXml(nfse.caminho_xml)} style={ui.actionSuccess}>
                                                        <FaFileCode />
                                                    </button>
                                                )}
                                                {nfse.status === 'Pendente' && (
                                                    <button title="Enviar" onClick={() => handleShowXml(nfse.caminho_xml)} style={ui.actionSend}>
                                                        <IoIosSend />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div style={ui.paginationContainer}>
                                <button onClick={goToPreviousPage} disabled={currentPage === 1} style={ui.paginationNav}>
                                    Anterior
                                </button>
                                {renderPageNumbers()}
                                <button onClick={goToNextPage} disabled={currentPage === totalPages} style={ui.paginationNav}>
                                    Próxima
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>


            <CreateNfseModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onCreated={fetchNfses}
                emitenteId={selectedEmitenteId}
            />

        </div>
    );
};

/** 🎨 UI – mesmo padrão do ClienteModal */
const ui = {
    page: {
        padding: 24,
        backgroundColor: '#f8fafc',
        minHeight: '95vh',
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        boxSizing: 'border-box',
        marginBottom: 40
    },
    headerBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 24,
        marginBottom: 24,
        border: '1px solid #e5e7eb',
        borderRadius: '16px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        color: '#fff',
    },
    headerTitle: {
        margin: 0,
        fontSize: '1.5rem',
        fontWeight: 600,
    },
    headerButton: {
        backgroundColor: 'rgba(255,255,255,0.15)',
        color: '#fff',
        padding: '10px 16px',
        borderRadius: 10,
        border: '2px solid rgba(255,255,255,0.25)',
        cursor: 'pointer',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
    },

    card: {
        backgroundColor: '#ffffff',
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        padding: 20,
        marginBottom: 24,
    },
    sectionTitle: {
        margin: '0 0 16px 0',
        fontSize: '1.125rem',
        fontWeight: 600,
        color: '#1f2937',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: 8,
    },
    sectionTitleTabela: {
        margin: '0 0 0 0',
        fontSize: '1.125rem',
        fontWeight: 600,
        color: '#1f2937',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: 5,
    },
    filtersRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 200,
        flex: 1,
    },
    label: {
        marginBottom: 6,
        fontWeight: 500,
        color: '#374151',
        fontSize: '0.875rem',
    },
    helpText: {
        fontSize: '0.75rem',
        color: '#6b7280',
        marginTop: 4,
        display: 'flex',
        alignItems: 'center',
    },
    input: {
        padding: '12px 16px',
        borderRadius: 8,
        border: '2px solid #e5e7eb',
        fontSize: '0.875rem',
        transition: 'all 0.2s ease',
        backgroundColor: '#ffffff',
        outline: 'none',
    },
    select: {
        padding: '12px 16px',
        borderRadius: 8,
        border: '2px solid #e5e7eb',
        fontSize: '0.875rem',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        outline: 'none',
        transition: 'all 0.2s ease',
    },
    buttonPrimary: {
        backgroundColor: '#667eea',
        color: '#ffffff',
        padding: '12px 16px',
        borderRadius: 8,
        border: '2px solid #667eea',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.875rem',
        display: 'flex',
        alignItems: 'center',
    },

    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: '0 8px',
    },
    th: {
        backgroundColor: '#e9ecef',
        padding: 8,
        textAlign: 'left',
        color: '#555',
        fontWeight: 'bold',
        fontSize: '0.85rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderBottom: '2px solid #dee2e6',
    },
    tr: {
        backgroundColor: '#fdfdfe',
        transition: 'background-color 0.2s ease',
        minHeight: 45,
    },
    td: {
        padding: 8,
        borderBottom: '1px solid #e9ecef',
        color: '#495057',
        fontSize: '0.8rem',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        lineHeight: 1.3,
    },
    tdActions: {
        padding: '6px 8px',
        borderBottom: '1px solid #e9ecef',
        display: 'flex',
        gap: 6,
        alignItems: 'center',
    },

    // Badges de status
    badgeGreen: {
        backgroundColor: '#d1fae5',
        color: '#065f46',
        padding: '4px 10px',
        borderRadius: 999,
        fontWeight: 700,
        fontSize: '0.75rem',
    },
    badgeRed: {
        backgroundColor: '#fee2e2',
        color: '#991b1b',
        padding: '4px 10px',
        borderRadius: 999,
        fontWeight: 700,
        fontSize: '0.75rem',
    },

    // Ações
    actionPrimary: {
        backgroundColor: '#3b82f6',
        color: '#fff',
        border: 'none',
        padding: 8,
        borderRadius: 8,
        cursor: 'pointer',
    },
    actionSuccess: {
        backgroundColor: '#10b981',
        color: '#fff',
        border: 'none',
        padding: 8,
        borderRadius: 8,
        cursor: 'pointer',
    },
    actionDanger: {
        backgroundColor: '#ef4444',
        color: '#fff',
        border: 'none',
        padding: 8,
        borderRadius: 8,
        cursor: 'pointer',
    },
    actionSend: {
        backgroundColor: '#c59650',
        color: '#fff',
        border: 'none',
        padding: 8,
        borderRadius: 8,
        cursor: 'pointer',
    },

    // Paginação
    paginationContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 20,
        gap: 10,
    },
    paginationNav: {
        backgroundColor: '#667eea',
        color: '#fff',
        padding: '10px 18px',
        borderRadius: 8,
        border: 'none',
        fontSize: '0.95rem',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
    paginationList: {
        display: 'flex',
        listStyle: 'none',
        margin: 0,
        padding: 0,
        gap: 6,
    },
    paginationItem: { cursor: 'pointer' },
    paginationButton: {
        backgroundColor: '#f0f2f5',
        color: '#333',
        padding: '10px 15px',
        borderRadius: 8,
        border: '1px solid #ddd',
        fontSize: '0.95rem',
        minWidth: 40,
        cursor: 'pointer',
    },
    paginationButtonActive: {
        backgroundColor: '#667eea',
        color: '#fff',
        borderColor: '#667eea',
    },

    muted: { color: '#6b7280' },
    errorText: { color: '#ef4444', fontWeight: 600 },
};

export default NfseControlScreen;
